import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { X, Save, UserPlus, ArrowUpRight } from "lucide-react";

/**
 * PersonFormDialog — add/edit a non-login staff member.
 *
 * Non-login "People" are surfaced ONLY as Asset assignees (Monitor →
 * "Stephanie", Workshop bench → "Faye"). Kept out of the login user
 * roster + PIN screen. Managers can later "Promote to login user" from
 * the Team page if roles change.
 */
const empty = {
  name: "",
  role: "",
  department: "",
  desk: "",
  notes: "",
};

const PersonFormDialog = ({ open, person, onClose, onSaved, onPromote }) => {
  const { departments } = useDepartments();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const editing = !!person?.id;

  useEffect(() => {
    if (!open) return;
    setForm(
      person?.id
        ? {
            name: person.name || "",
            role: person.role || "",
            department: person.department || "",
            desk: person.desk || "",
            notes: person.notes || "",
          }
        : empty,
    );
  }, [open, person?.id]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/people/${person.id}`, form);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/people", form);
        toast.success(`${form.name} added`);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="person-form-overlay"
    >
      <div
        className="nb-card p-6 w-full max-w-md bg-white"
        onClick={(e) => e.stopPropagation()}
        data-testid="person-form-dialog"
      >
        <div className="flex justify-between items-start mb-1">
          <div>
            <div className="label-eyebrow">Non-login staff member</div>
            <h3 className="font-display text-2xl mt-1">
              {editing ? "Edit person" : "New person"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
            data-testid="person-form-close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="text-xs text-[#607087] mb-4">
          Won't appear on the iPad login screen. Purely for tagging Asset
          ownership.
        </div>

        <div className="flex flex-col gap-3">
          <label>
            <div className="label-eyebrow mb-1">Name *</div>
            <input
              data-testid="person-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Stephanie Ryan"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Role</div>
            <input
              data-testid="person-role-input"
              className="nb-input"
              value={form.role}
              onChange={(e) => set("role", e.target.value)}
              placeholder="Receptionist · Podiatrist · Workshop assistant"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label>
              <div className="label-eyebrow mb-1">Department</div>
              <select
                data-testid="person-dept-select"
                className="nb-input"
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
              >
                <option value="">— none —</option>
                {(departments || []).map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div className="label-eyebrow mb-1">Desk / bench</div>
              <input
                data-testid="person-desk-input"
                className="nb-input"
                value={form.desk}
                onChange={(e) => set("desk", e.target.value)}
                placeholder="Reception 2, Bench A"
              />
            </label>
          </div>
          <label>
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="person-notes-input"
              className="nb-input min-h-[60px]"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything the workshop should know when picking up their gear."
            />
          </label>

          {editing && onPromote && !person?.promoted_user_id && (
            <button
              type="button"
              data-testid="person-promote-btn"
              onClick={() => onPromote(person)}
              className="nb-btn bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A] px-4 py-2 text-sm flex items-center gap-2 justify-center"
              title="Turn this Person into a full login user (assigns a PIN, keeps their asset links)"
            >
              <ArrowUpRight size={14} strokeWidth={2.5} />
              Promote to login user…
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="nb-btn bg-white px-4 py-2"
            disabled={busy}
            data-testid="person-form-cancel"
          >
            Cancel
          </button>
          <button
            data-testid="person-form-save"
            onClick={submit}
            disabled={busy || !form.name.trim()}
            className="nb-btn nb-btn-primary px-5 py-2 flex items-center gap-2 font-bold"
          >
            {editing ? <Save size={14} strokeWidth={2.5} /> : <UserPlus size={14} strokeWidth={2.5} />}
            {editing ? "Save" : "Add person"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonFormDialog;
